import quickJsVariant from '@jitl/quickjs-singlefile-browser-release-sync'
import {
  memoizePromiseFactory,
  newQuickJSWASMModuleFromVariant,
  shouldInterruptAfterDeadline,
  type QuickJSContext,
  type QuickJSWASMModule
} from 'quickjs-emscripten-core'

/**
 * A condition either answered with a group id, or it didn't. We don't say why
 * it didn't, because there's nothing a caller can do differently either way -
 * the route it belongs to gets skipped regardless.
 */
export type ExpressionEvaluation = { value: string } | { failed: true }

export type ExpressionEvaluator = (args: {
  expression: string
  input: Record<string, unknown>
}) => ExpressionEvaluation

/**
 * Deliberately mean. A condition compares a handful of values it was handed, so
 * it has no reason to want more than this, and anything that does has gone
 * wrong. Raise them if a real condition ever starts hitting one.
 */
const MEMORY_LIMIT_BYTES = 1024 * 1024
const MAX_STACK_SIZE_BYTES = 1024 * 320
const TIMEOUT_MS = 100

export type ExpressionEvaluatorHandle = {
  evaluate: ExpressionEvaluator
  dispose: () => void
}

/**
 * Opens one sandbox to run a whole run's conditions in, or nothing at all if
 * the engine hasn't been loaded. Whoever opens it has to close it - it's a
 * chunk of WebAssembly memory rather than a normal object, so nothing tidies it
 * up on its own.
 *
 * Wait for `loadSimulationSandbox` once before calling this. The engine is
 * WebAssembly and has to be fetched and compiled, which can't be done without
 * waiting - but that's the only part that does, and every condition after it is
 * a plain function call.
 */
export const setupExpressionEvaluator = ():
  | ExpressionEvaluatorHandle
  | undefined => {
  if (!loadedQuickJs) return undefined

  const vm = loadedQuickJs.newContext()

  vm.runtime.setMemoryLimit(MEMORY_LIMIT_BYTES)
  vm.runtime.setMaxStackSize(MAX_STACK_SIZE_BYTES)

  return {
    evaluate: args => evaluateExpression({ vm, ...args }),
    dispose: () => {
      if (vm.alive) vm.dispose()
    }
  }
}

/**
 * Fetches and compiles the engine, once per session. Says whether it worked, so
 * a caller can tell "still loading" apart from "this browser can't", rather
 * than waiting forever on an answer that isn't coming.
 */
export const loadSimulationSandbox = async (): Promise<boolean> => {
  try {
    loadedQuickJs = await loadQuickJs()
    return true
  } catch {
    return false
  }
}

export const isSimulationSandboxLoaded = (): boolean => Boolean(loadedQuickJs)

/**
 * Imported from `quickjs-emscripten-core` rather than `quickjs-emscripten`,
 * because the latter brings all four builds of the engine with it and the
 * production build then writes out four `.wasm` files we never load.
 *
 * The build we do use has its WebAssembly inside the JavaScript rather than
 * beside it, so there's no second file to go and fetch and nothing to get wrong
 * about where it lives.
 *
 * Compiling that WebAssembly is the one thing here that has to be waited on -
 * there's no way to hand a browser a module and get an instance back without
 * it. Memoised so a second caller joins the first rather than starting again.
 */
const loadQuickJs = memoizePromiseFactory(() =>
  newQuickJSWASMModuleFromVariant(quickJsVariant)
)

/**
 * Kept once loaded, so running a condition never has to wait. There's only ever
 * one engine, and loading it again costs far more than holding on to it.
 */
let loadedQuickJs: QuickJSWASMModule | undefined

/**
 * Nothing from this side is handed into the sandbox. The values the condition
 * reads are written into the code as plain JSON, so there's no way for it to
 * reach back out - no `fetch`, no `console`, nothing of ours it could hold on
 * to.
 */
const evaluateExpression = ({
  vm,
  expression,
  input
}: {
  vm: QuickJSContext
  expression: string
  input: Record<string, unknown>
}): ExpressionEvaluation => {
  if (!vm.alive) return { failed: true }

  const serialisedInput = serialiseInput(input)
  if (serialisedInput === undefined) return { failed: true }

  // A fixed point in time rather than a countdown, so it's set again for each
  // condition. Set once, it would pass while we were still working and stop
  // every condition after it before it had started.
  vm.runtime.setInterruptHandler(
    shouldInterruptAfterDeadline(Date.now() + TIMEOUT_MS)
  )

  try {
    const result = vm.evalCode(`var $ = ${serialisedInput}; ${expression}`)

    if (result.error) {
      result.error.dispose()
      return { failed: true }
    }

    const value = result.value.consume(handle => vm.dump(handle))

    return typeof value === 'string' ? { value } : { failed: true }
  } catch {
    // Running out of memory can take down the call itself rather than coming
    // back as an error we can read. Either way the answer is the same, and
    // nothing gets out of here.
    return { failed: true }
  }
}

/**
 * `JSON.stringify` already does what we want with the awkward cases - it drops
 * anything undefined and writes dates out as strings. It gives up on a value
 * that contains itself, which an executor output shouldn't but might, and we
 * treat that as a condition we couldn't run rather than letting it throw.
 */
const serialiseInput = (input: Record<string, unknown>): string | undefined => {
  try {
    return JSON.stringify(input) ?? undefined
  } catch {
    return undefined
  }
}
